import React, { useState } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  marketingColor,
  marketingFont,
  marketingLine,
  marketingShadow,
} from '../../theme/marketingTokens';

export type FeatureId = 'ingest' | 'sync' | 'rebate' | 'map' | 'catalog';

const FEATURE_IDS: FeatureId[] = ['ingest', 'sync', 'rebate', 'map', 'catalog'];

/** Terminal-window filename shown in the panel chrome, per feature. */
const PANEL_TITLES: Record<FeatureId, string> = {
  ingest: 'motzu-ingest — terminal',
  sync: 'channel-status — live',
  rebate: 'rebates — matched',
  map: 'map-monitor — scanning',
  catalog: 'catalog — 12,480 products',
};

const mono = (size: number, color: string) =>
  ({ fontFamily: marketingFont.mono, fontSize: size, color } as const);

/** Shared row shell for the sync / rebate / MAP panels. */
const panelRowSx = (border: string) =>
  ({
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    backgroundColor: marketingColor.ink,
    border: `1px solid ${border}`,
    borderRadius: '6px',
    px: 1.75,
    py: 1.5,
  } as const);

const IngestPanel: React.FC = () => {
  const { t } = useTranslation() as { t: (key: string) => string };
  return (
    <Box sx={{ ...mono(12.5, marketingColor.textSecondary), lineHeight: 2 }}>
      <Box>
        <Box component="span" sx={{ color: marketingColor.accent }}>$</Box> motzu ingest
        --source sony-feed
      </Box>
      <Box sx={{ color: marketingColor.textMuted }}>{t('public.features.panels.ingest.connecting')}</Box>
      <Box>
        ✓ <Box component="span" sx={{ color: marketingColor.text }}>1,820 {t('public.features.panels.ingest.productsFound')}</Box>
      </Box>
      <Box sx={{ color: marketingColor.textMuted }}>{t('public.features.panels.ingest.normalizing')}</Box>
      <Box>
        ✓ <Box component="span" sx={{ color: marketingColor.text }}>{t('public.features.panels.ingest.specsMapped')}</Box>{' '}
        <Box component="span" sx={{ color: marketingColor.textMuted }}>(33MP, 4K60, IBIS...)</Box>
      </Box>
      <Box>
        ✓ <Box component="span" sx={{ color: marketingColor.text }}>12 {t('public.features.panels.ingest.newSkus')}</Box>{' '}
        <Box component="span" sx={{ color: marketingColor.textMuted }}>
          · 214 {t('public.features.panels.ingest.updated')} · 0 {t('public.features.panels.ingest.errors')}
        </Box>
      </Box>
      <Box>
        <Box component="span" sx={{ color: marketingColor.accent }}>{t('public.features.panels.ingest.done')}</Box>{' '}
        <Box component="span" sx={{ color: marketingColor.textMuted }}>{t('public.features.panels.ingest.in')} 42s</Box>
        <Box
          component="span"
          aria-hidden
          sx={{
            display: 'inline-block',
            width: 8,
            height: 14,
            backgroundColor: marketingColor.accent,
            ml: 0.75,
            verticalAlign: 'middle',
            animation: 'motzuBlink 1.1s step-end infinite',
          }}
        />
      </Box>
    </Box>
  );
};

const SYNC_CHANNELS = [
  { name: 'Shopify', stores: 18, ago: '4s', pulse: '1.4s' },
  { name: 'WooCommerce', stores: 9, ago: '11s', pulse: '1.9s' },
  { name: 'BigCommerce', stores: 4, ago: '2s', pulse: '1.2s' },
  { name: 'Lightspeed', stores: 3, ago: '20s', pulse: '2.3s' },
];

const SyncPanel: React.FC = () => {
  const { t } = useTranslation() as { t: (key: string) => string };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {SYNC_CHANNELS.map((channel) => (
        <Box key={channel.name} sx={panelRowSx('rgba(95,226,138,0.2)')}>
          <Box
            aria-hidden
            sx={{
              width: 8,
              height: 8,
              flexShrink: 0,
              borderRadius: '999px',
              backgroundColor: marketingColor.accent,
              boxShadow: `0 0 8px ${marketingColor.accent}`,
              animation: `motzuBlink ${channel.pulse} ease-in-out infinite`,
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                fontFamily: marketingFont.display,
                fontSize: 14,
                fontWeight: 700,
                color: marketingColor.text,
              }}
            >
              {channel.name}
            </Box>
            <Box sx={mono(11, marketingColor.textMuted)}>
              {channel.stores} {t('public.features.panels.sync.stores')} ·{' '}
              {t('public.features.panels.sync.lastPush')} {channel.ago}
            </Box>
          </Box>
          <Box sx={mono(11, marketingColor.accent)}>{t('public.features.panels.sync.live')}</Box>
        </Box>
      ))}
    </Box>
  );
};

const REBATE_ROWS = [
  { name: 'Sony Alpha 7 IV', sku: 'ILCE7M4MB', was: '$2,699', now: '$2,199', badge: '-$500' },
  { name: 'Canon EOS R6 II', sku: 'CAN-R6II-002', was: '$2,499', now: '$2,299', badge: '-$200' },
  { name: 'Fujifilm X-T5', sku: 'FUJ-XT5-004', was: '$1,799', now: '$1,699', badge: '-$100' },
  { name: 'Nikon Zf', sku: 'NIK-ZF-009', was: '$2,099', now: '$1,999', badge: '-$100' },
];

const RebatePanel: React.FC = () => {
  const { t } = useTranslation() as { t: (key: string) => string };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {REBATE_ROWS.map((row) => (
        <Box key={row.sku} sx={panelRowSx(marketingLine.hairline)}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                fontFamily: marketingFont.display,
                fontSize: 13.5,
                fontWeight: 700,
                color: marketingColor.text,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {row.name}
            </Box>
            <Box sx={mono(10.5, marketingColor.textMuted)}>{row.sku}</Box>
          </Box>
          <Box sx={{ ...mono(11.5, marketingColor.textMuted), textDecoration: 'line-through' }}>
            {row.was}
          </Box>
          <Box sx={{ ...mono(12.5, marketingColor.text), fontWeight: 700 }}>{row.now}</Box>
          <Box
            sx={{
              ...mono(10.5, marketingColor.accent),
              border: `1px solid ${marketingLine.hairlineStrong}`,
              borderRadius: '3px',
              px: 0.9,
              py: 0.25,
              whiteSpace: 'nowrap',
            }}
          >
            {row.badge} {t('public.features.panels.rebate.badge')}
          </Box>
        </Box>
      ))}
      <Box sx={{ ...mono(11, marketingColor.textMuted), textAlign: 'right' }}>
        {t('public.features.panels.rebate.footer')}
      </Box>
    </Box>
  );
};

const MAP_ROWS = [
  { name: 'Sony Alpha 7 IV', map: '$2,199', listed: '$2,199', ok: true },
  { name: 'Leica Q3', map: '$5,995', listed: '$5,995', ok: true },
  { name: 'Nikon Z6 III', map: '$2,499', listed: '$2,389', ok: false },
  { name: 'Canon EOS R8', map: '$1,499', listed: '$1,499', ok: true },
];

const MapPanel: React.FC = () => {
  const { t } = useTranslation() as { t: (key: string) => string };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {MAP_ROWS.map((row) => {
        const tone = row.ok ? marketingColor.accent : marketingColor.error;
        return (
          <Box
            key={row.name}
            sx={panelRowSx(row.ok ? marketingLine.hairline : marketingLine.error)}
          >
            <Box sx={{ ...mono(13, tone), flexShrink: 0 }}>{row.ok ? '✓' : '!'}</Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  fontFamily: marketingFont.display,
                  fontSize: 13.5,
                  fontWeight: 700,
                  color: marketingColor.text,
                }}
              >
                {row.name}
              </Box>
              <Box sx={mono(10.5, marketingColor.textMuted)}>
                MAP {row.map} · {t('public.features.panels.map.listed')} {row.listed}
              </Box>
            </Box>
            <Box sx={{ ...mono(10.5, tone), textAlign: 'right' }}>
              {row.ok
                ? t('public.features.panels.map.compliant')
                : t('public.features.panels.map.violation')}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

const CATALOG_ROWS = [
  { name: 'Sony Alpha 7 IV', price: '$2,199', stock: '34', channels: '2' },
  { name: 'Canon EOS R6 II', price: '$2,299', stock: '12', channels: '1' },
  { name: 'Fujifilm X-T5', price: '$1,699', stock: '51', channels: '2' },
  { name: 'Leica Q3', price: '$5,995', stock: '3', channels: '1' },
  { name: 'Nikon Zf', price: '$1,999', stock: '15', channels: '1' },
];

const CATALOG_GRID = '2fr 1fr 1fr 1fr';

const CatalogPanel: React.FC = () => {
  const { t } = useTranslation() as { t: (key: string) => string };
  return (
    <Box sx={{ border: `1px solid ${marketingLine.hairline}`, borderRadius: '6px', overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: CATALOG_GRID,
          px: 1.75,
          py: 1.25,
          backgroundColor: marketingColor.ink,
          borderBottom: `1px solid ${marketingLine.hairline}`,
        }}
      >
        {['product', 'price', 'stock', 'channels'].map((key) => (
          <Box key={key} sx={{ ...mono(10, marketingColor.textMuted), textTransform: 'uppercase' }}>
            {t(`public.features.panels.catalog.${key}`)}
          </Box>
        ))}
      </Box>
      {CATALOG_ROWS.map((row) => (
        <Box
          key={row.name}
          sx={{
            display: 'grid',
            gridTemplateColumns: CATALOG_GRID,
            alignItems: 'center',
            px: 1.75,
            py: 1.4,
            borderBottom: '1px solid rgba(95,226,138,0.08)',
          }}
        >
          <Box
            sx={{
              fontFamily: marketingFont.display,
              fontSize: 12.5,
              fontWeight: 700,
              color: marketingColor.text,
            }}
          >
            {row.name}
          </Box>
          <Box sx={{ ...mono(11.5, marketingColor.accent), fontVariantNumeric: 'tabular-nums' }}>
            {row.price}
          </Box>
          <Box sx={{ ...mono(11.5, marketingColor.textSecondary), fontVariantNumeric: 'tabular-nums' }}>
            {row.stock}
          </Box>
          <Box sx={{ ...mono(11.5, marketingColor.textSecondary), fontVariantNumeric: 'tabular-nums' }}>
            {row.channels}
          </Box>
        </Box>
      ))}
      <Box sx={{ ...mono(10.5, marketingColor.textMuted), px: 1.75, py: 1.25 }}>
        {t('public.features.panels.catalog.footer')}
      </Box>
    </Box>
  );
};

const PANELS: Record<FeatureId, React.FC> = {
  ingest: IngestPanel,
  sync: SyncPanel,
  rebate: RebatePanel,
  map: MapPanel,
  catalog: CatalogPanel,
};

/**
 * The Features page explorer: a single-open accordion on the left driving a
 * sticky terminal-style demo panel on the right.
 */
export const FeatureExplorer: React.FC = () => {
  const { t } = useTranslation() as { t: (key: string, options?: any) => any };
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up('md'));
  const [open, setOpen] = useState<FeatureId>('ingest');
  const Panel = PANELS[open];

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 5,
        alignItems: 'flex-start',
        flexWrap: 'wrap',
      }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: { xs: '100%', md: 320 },
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
        }}
      >
        {FEATURE_IDS.map((id, index) => {
          const expanded = open === id;
          const bullets = t(`public.features.items.${id}.bullets`, {
            returnObjects: true,
          }) as string[];
          return (
            <Box
              key={id}
              component="button"
              type="button"
              aria-expanded={expanded}
              onClick={() => setOpen(id)}
              sx={{
                textAlign: 'left',
                width: '100%',
                font: 'inherit',
                cursor: 'pointer',
                border: `1px solid ${expanded ? marketingColor.accent : marketingLine.hairline}`,
                backgroundColor: expanded ? marketingColor.surface : 'transparent',
                borderRadius: '6px',
                px: 2.75,
                py: 2.5,
                transition: 'border-color 0.25s ease, background-color 0.25s ease',
                '&:hover': { borderColor: marketingLine.hairlineHover },
                '&:focus-visible': {
                  outline: `2px solid ${marketingColor.accent}`,
                  outlineOffset: 2,
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                <Box
                  sx={{
                    ...mono(13, expanded ? marketingColor.accent : marketingColor.textMuted),
                    fontWeight: 700,
                  }}
                >
                  {String(index + 1).padStart(2, '0')}
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    fontFamily: marketingFont.display,
                    fontSize: 17,
                    fontWeight: 700,
                    color: expanded ? marketingColor.text : marketingColor.textSecondary,
                  }}
                >
                  {t(`public.features.items.${id}.title`)}
                </Box>
                <Box
                  aria-hidden
                  sx={mono(14, expanded ? marketingColor.accent : marketingColor.textMuted)}
                >
                  {expanded ? '−' : '+'}
                </Box>
              </Box>
              {expanded ? (
                <Box sx={{ mt: 1.5 }}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: marketingColor.textSecondary,
                      lineHeight: 1.6,
                      mb: 1.75,
                    }}
                  >
                    {t(`public.features.items.${id}.description`)}
                  </Typography>
                  {(Array.isArray(bullets) ? bullets : []).map((bullet) => (
                    <Box
                      key={bullet}
                      sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 1.1 }}
                    >
                      <Box
                        aria-hidden
                        sx={{ ...mono(13, marketingColor.accent), fontWeight: 700, flexShrink: 0 }}
                      >
                        &gt;
                      </Box>
                      <Box sx={{ fontSize: 13.5, color: marketingColor.text }}>{bullet}</Box>
                    </Box>
                  ))}
                </Box>
              ) : null}
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          flex: 1.15,
          minWidth: { xs: '100%', md: 360 },
          // Sticky only where there is a second column to stick beside.
          position: wide ? 'sticky' : 'static',
          top: 100,
        }}
      >
        <Box
          sx={{
            backgroundColor: marketingColor.surface,
            border: '1px solid rgba(95,226,138,0.25)',
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: marketingShadow.panel,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${marketingLine.hairline}`,
            }}
          >
            {[marketingColor.surfaceStrong, marketingColor.surfaceStrong, marketingColor.accent].map(
              (dot, i) => (
                <Box
                  key={i}
                  aria-hidden
                  sx={{ width: 9, height: 9, borderRadius: '999px', backgroundColor: dot }}
                />
              )
            )}
            <Box sx={{ ...mono(11, marketingColor.textMuted), ml: 1 }}>{PANEL_TITLES[open]}</Box>
          </Box>
          <Box sx={{ p: 2.75, minHeight: 380 }}>
            <Panel />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
